/**
 * HelpButton — платформенная кнопка «?» в шапке страницы (§9.5 ТЗ). Открывает
 * боковую панель со справкой: зачем · какие задачи · как пользоваться (+ глоссарий).
 * Наполняется контентом (HelpContent); механика годится для любого раздела.
 * Закрытие — Esc / клик по фону.
 */

import { useEffect } from 'react';
import { useState } from 'react';
import { IconButton, Icon } from '@/shared/ui';
import type { HelpContent } from '@/help/seoHelp';

export function HelpButton({ help }: { help: HelpContent | undefined }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!help) return null;

  return (
    <>
      <IconButton icon="Help" tip="Справка по разделу" onClick={() => setOpen(true)} />
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.32)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            role="dialog"
            aria-label={help.title}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(440px, 92vw)',
              height: '100%',
              overflowY: 'auto',
              background: 'var(--surface, #fff)',
              borderLeft: '1px solid var(--border)',
              padding: '20px 22px',
              boxShadow: '-8px 0 24px rgba(15,23,42,0.12)',
            }}
          >
            <div className="row spread center" style={{ marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>{help.title}</h3>
              <IconButton icon="XCircle" tip="Закрыть" onClick={() => setOpen(false)} />
            </div>

            <HelpSection icon="Sparkles" title="Зачем этот раздел">
              <p style={{ margin: 0, lineHeight: 1.55 }}>{help.why}</p>
            </HelpSection>

            <HelpSection icon="Check" title="Какие задачи решает">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                {help.tasks.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </HelpSection>

            <HelpSection icon="ArrowRight" title="Как пользоваться">
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                {help.how.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ol>
            </HelpSection>

            {help.glossary && help.glossary.length > 0 && (
              <HelpSection icon="Hash" title="Термины">
                <dl style={{ margin: 0 }}>
                  {help.glossary.map((g, i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 700 }}>{g.term}</span>
                      <span className="muted"> — {g.def}</span>
                    </div>
                  ))}
                </dl>
              </HelpSection>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function HelpSection({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div className="row center" style={{ gap: 7, marginBottom: 6 }}>
        <Icon name={icon as never} size={16} />
        <span style={{ fontWeight: 700, fontSize: 13.5 }}>{title}</span>
      </div>
      {children}
    </section>
  );
}
